import React, { useEffect, useReducer, useRef } from 'react';

import {
    DEFAULT_ACTION_HEX_POSITION,
} from '../../Game/GamePlayers/GamePlayerController';
import { getTextureUrl } from '../../TwoD/Common/TextureTools';

import './ActionHexDial.css';

// Texture00125.png — the same hexagon the legacy 2D surface
// (ActionHex2d) uses. Resolves to ${TEXTURES_URL}Texture00125.png via
// getTextureUrl. One source of truth for the hex art id.
const ACTION_HEX_TEXTURE_ID = 125;

// ActionHexDial — a spinning Action Hex dial for the Desktop preset's
// ActionWindow. A *second* input surface for the same state the
// Dashboard ActionHex slider drives: both dispatch / consume
// action-hex-event through the seated controller, so rotating one
// re-renders the other for free. Left-half tap rotates position -1,
// right-half +1, mod 12. Locked hex suppresses clicks (and dims).
//
// Mirrors the legacy ActionHex2d's live-state discipline: every state
// field is read live from the seated controller for the local seat
// (its reference mutates synchronously under the local-intent guard
// in updateGamePlayerData, while the React prop reference can diverge
// and lag), with a fall-back to the React prop only for non-seated
// (observer) mounts.
//
// The visual rotation is tracked as CONTINUOUS degrees in a ref that
// is never wrapped to mod 360, so a step from pos 11 → pos 0 advances
// +30° rather than unwinding -330°. The mod-12 position lives only in
// the dispatched event detail + currentPositionRef.
export function ActionHexDial(props) {
    const { app, gamePlayerData } = props;
    const seat = gamePlayerData.seat;

    // propsRef keeps the listeners reading current props without
    // re-installing on every render. Matches ActionHex.jsx discipline.
    const propsRef = useRef(props);
    propsRef.current = props;

    // Seed BOTH refs in the initializer, before the first render
    // returns, so production sees no 0deg→Ndeg snap on mount (the same
    // class of race the slider's onSwiper seed dance fixed — simpler
    // here because there is no Swiper).
    const initialPosition =
        (gamePlayerData.action_hex_position ?? DEFAULT_ACTION_HEX_POSITION);
    const continuousDegRef = useRef(30 * initialPosition);
    const currentPositionRef = useRef(initialPosition);

    const [, forceRender] = useReducer((x) => x + 1, 0);

    const effectiveGamePlayerData = () => {
        // Seated controller is the source of truth for the local seat;
        // its reference mutates synchronously under the local-intent
        // guard while the prop reference can diverge / lag. Fall back
        // to the prop only for non-seated (observer) mounts. Mirrors
        // ActionHex2d._effectiveGamePlayerData exactly.
        const seated = app && app.game && app.game.seatedController;
        const gp = propsRef.current.gamePlayerData;
        if (seated && gp && gp.seat === seated.seat) {
            return seated.gamePlayerData;
        }
        return gp;
    };

    const currentPosition = () => {
        const gp = effectiveGamePlayerData();
        const raw = gp ? gp.action_hex_position : null;
        if (raw === null || raw === undefined) {
            return DEFAULT_ACTION_HEX_POSITION;
        }
        return raw;
    };

    const isLocked = () => {
        const gp = effectiveGamePlayerData();
        return !!(gp && gp.action_hex_locked);
    };

    const handleFrameClick = (e) => {
        // Lock read live — match the slider's isHexLocked discipline.
        if (isLocked()) return;
        // onClick lives on the (un-rotated) frame, so these coords are
        // stable: transform: rotate() on the inner img rotates the box
        // getBoundingClientRect reports, which would make hit-test math
        // on the img itself jump around as the hex spins.
        const rect = e.currentTarget.getBoundingClientRect();
        const relX = e.clientX - rect.left;
        const isLeftHalf = relX < rect.width / 2;
        const current = currentPosition();
        const next = isLeftHalf
            ? (current + 11) % 12   // -1 mod 12, never negative
            : (current + 1) % 12;
        continuousDegRef.current += isLeftHalf ? -30 : 30;
        currentPositionRef.current = next;
        // Route through action-hex-event: the seated controller is the
        // single owner of local intent + debounced wire broadcast. No
        // direct API call here. The slider listens to the same event
        // and re-snaps without echoing back.
        window.dispatchEvent(new CustomEvent('action-hex-event', {
            detail: { seat: seat, actionHexPosition: next },
        }));
        forceRender();
    };

    useEffect(() => {
        const seatEventName = 'game-player-' + seat + '-event';

        // Incoming rotate intent from another surface (the slider, or
        // the legacy 2D hex). Apply on the continuous-degrees ref with
        // the shortest arc so a wrap never unwinds the long way round.
        // No echo: our own dispatch lands here too but is dropped by
        // the position-equality guard (handleFrameClick sets
        // currentPositionRef before dispatching).
        const actionHexListener = (e) => {
            if (seat !== e.detail.seat) return;
            const position = e.detail.actionHexPosition;
            if (position === currentPositionRef.current) return;
            let delta = (position - currentPositionRef.current + 12) % 12;
            if (delta > 6) delta -= 12;   // shortest arc; 6 ties → +6
            continuousDegRef.current += 30 * delta;
            currentPositionRef.current = position;
            forceRender();
        };

        // Per-seat backend fan-out. Opponent seats get their position
        // here (not via action-hex-event), and lock/unlock state
        // changes arrive here too — repaint so the dimming + rotation
        // track. Position synced with the same shortest-arc rule; the
        // equality guard makes the local seat's own echo a no-op.
        const seatEventListener = (e) => {
            const gp = e.detail;
            if (!gp) return;
            const position = gp.action_hex_position;
            if (position !== null && position !== undefined
                    && position !== currentPositionRef.current) {
                let delta =
                    (position - currentPositionRef.current + 12) % 12;
                if (delta > 6) delta -= 12;
                continuousDegRef.current += 30 * delta;
                currentPositionRef.current = position;
            }
            forceRender();
        };

        window.addEventListener('action-hex-event', actionHexListener);
        window.addEventListener(seatEventName, seatEventListener);
        return () => {
            window.removeEventListener(
                'action-hex-event', actionHexListener);
            window.removeEventListener(seatEventName, seatEventListener);
        };
    // seat is stable for the mount; listeners capture via propsRef.
    }, [seat]);

    const locked = isLocked();
    const frameClass = 'action-hex-dial-frame'
        + (locked ? ' action-hex-dial--locked' : '');

    return (
        <div className={frameClass}
             data-testid={'action-hex-dial'}
             onClick={handleFrameClick}>
            <img
                src={getTextureUrl(ACTION_HEX_TEXTURE_ID)}
                alt={'action-hex'}
                className={'action-hex-dial-image'}
                style={{
                    transform: `rotate(${continuousDegRef.current}deg)`,
                }}/>
        </div>
    );
}

export default ActionHexDial;
